import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, Subject } from 'rxjs';
import { InputModel } from 'gast-onset-ng2';
import { CTestDto } from './dto/c-test.dto';
import { CTestAntwortDto } from './dto/c-test-antwort.dto';
import { CTestScoreDto } from './dto/c-test-score.dto';

@Injectable({
  providedIn: 'root',
})
export class PlacementTestsService {

  readonly #http = inject(HttpClient);

  token = '';

  // ── UI state (virtual keyboard / input navigation) ──────────────────────

  inputNumber = 0;
  inputs: InputModel[] = [];

  readonly insertSpecialCharacterToInput$$ = new Subject<string>();
  readonly removeLastCharacter$$ = new Subject<void>();

  setActiveInputNumber(nr: number): void {
    this.inputNumber = nr;
  }

  setSpecialCharacterToInput(character: string): void {
    this.insertSpecialCharacterToInput$$.next(character);
  }

  removeLastCharacterFromInput(): void {
    this.removeLastCharacter$$.next();
  }

  // ── HTTP ─────────────────────────────────────────────────────────────────

  #baseUrl(serviceUrl: string): string {
    return `${serviceUrl}api/placement-tests`;
  }

  #headers(): { headers: HttpHeaders } {
    return { headers: new HttpHeaders({ Authorization: `Bearer ${this.token}` }) };
  }

  getNextTask(serviceUrl: string, bookingId: number): Observable<CTestDto> {
    return this.#http.get<CTestDto>(
      `${this.#baseUrl(serviceUrl)}/bookings/${bookingId}/next-task`,
      this.#headers()
    );
  }

  saveTaskAnswers(serviceUrl: string, bookingId: number, answers: CTestAntwortDto[]): Observable<void> {
    return this.#http.post<void>(
      `${this.#baseUrl(serviceUrl)}/bookings/${bookingId}/tasks`,
      answers,
      this.#headers()
    );
  }

  getScore(serviceUrl: string, bookingId: number): Observable<CTestScoreDto> {
    return this.#http.get<CTestScoreDto>(
      `${this.#baseUrl(serviceUrl)}/bookings/${bookingId}/tasks`,
      this.#headers()
    );
  }

  updateTaskStartTime(serviceUrl: string, bookingId: number, taskId: number): Observable<void> {
    return this.#http.put<void>(
      `${this.#baseUrl(serviceUrl)}/bookings/${bookingId}/tasks/${taskId}/start-time`,
      null,
      this.#headers()
    );
  }

  updateTaskEndTime(serviceUrl: string, bookingId: number, taskId: number): Observable<void> {
    return this.#http.put<void>(
      `${this.#baseUrl(serviceUrl)}/bookings/${bookingId}/tasks/${taskId}/end-time`,
      null,
      this.#headers()
    );
  }

  updateBrowserRefreshCount(serviceUrl: string, bookingId: number): Observable<void> {
    return this.#http.put<void>(
      `${this.#baseUrl(serviceUrl)}/bookings/${bookingId}/browser-refresh-count`,
      null,
      this.#headers()
    );
  }

  updateResultShownTime(serviceUrl: string, bookingId: number): Observable<void> {
    return this.#http.put<void>(
      `${this.#baseUrl(serviceUrl)}/bookings/${bookingId}/result-shown-time`,
      null,
      this.#headers()
    );
  }

  sendStudyGuideTestResult(serviceUrl: string, bookingId: number): Observable<void> {
    return this.#http.post<void>(
      `${this.#baseUrl(serviceUrl)}/bookings/${bookingId}/send-result`,
      null,
      this.#headers()
    );
  }
}
